import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import { Grid, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import equal from "fast-deep-equal";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { DimensionDescriptor, MeasureField } from "../../../../../../../../../api/types/svTypes.ts";
import { selectDimensions } from "../../../../../store/selectors/metaDataSelectors.ts";

interface Props {
  measure: MeasureField;
  onUpdate: (value: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  readonly: boolean;
}

export default function TransferFieldMenuItem({ measure, onUpdate, readonly }: Props) {
  const dimensions = useSelector(selectDimensions);

  const option = useMemo(() => getOption(measure), [measure]);

  const onChangeValue = useCallback(
    (newValue: Values) => {
      const customConditions = rebuildCustomConditions(newValue, measure, dimensions);
      onUpdate({ customConditions, useCustomConditions: true }, false);
    },
    [measure, dimensions, onUpdate]
  );

  return (
    <Grid container sx={{ alignItems: "center", width: "100%" }}>
      <Typography>Transferred Balance</Typography>
      <HorizontalFill />
      <ToggleButtonGroup
        exclusive
        color="primary"
        value={option}
        fullWidth
        sx={{ width: 208 }}
        onChange={(_, value: Values | null) => {
          if (value !== null) onChangeValue(value);
        }}
        disabled={readonly}
      >
        {possibleOptions.map((item) => (
          <ToggleButton key={item.value} value={item.value} sx={{ py: 0.3, fontWeight: 400 }}>
            {item.text}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Grid>
  );
}

enum Values {
  On = "on",
  Off = "off",
  Ignore = "ignore",
}

const TransferValue = "ITRANSF";
const SourceCodeDimensionName = "SourceCode";

const possibleOptions = [
  { value: Values.On, text: "On" },
  { value: Values.Off, text: "Off" },
  { value: Values.Ignore, text: "Ignore" },
];

function getOption(measure: MeasureField) {
  if (!measure.config.customConditions || measure.config.useCustomConditions !== true) {
    return Values.Ignore;
  }
  const sourceCodeCondition = measure.config.customConditions.find(
    (cc) => cc.filter.dimensionName === SourceCodeDimensionName
  );
  if (sourceCodeCondition === undefined) return Values.Ignore;
  if (equal(sourceCodeCondition.filter.values, [TransferValue])) {
    return sourceCodeCondition.filter.equalityType === ConditionEqualityType.NotEqual ? Values.Off : Values.On;
  }
  return Values.Ignore;
}

function rebuildCustomConditions(newValue: Values, measure: MeasureField, dimensions: DimensionDescriptor[]) {
  const customConditions = structuredClone(measure.config.customConditions || []);

  const changeCondition = (conditionType: ConditionEqualityType) => {
    let sourceCodeCondition = customConditions?.find((cc) => cc.filter.dimensionName === SourceCodeDimensionName);
    if (sourceCodeCondition === undefined) {
      const dimension = dimensions.find((d) => d.name === SourceCodeDimensionName);
      if (dimension === undefined) return;
      sourceCodeCondition = {
        filter: { dimensionName: dimension.name, values: [TransferValue], equalityType: conditionType },
        guid: generateGuid(),
      };
      customConditions.push(sourceCodeCondition);
    } else {
      sourceCodeCondition.filter.equalityType = conditionType;
      sourceCodeCondition.filter.values = [TransferValue];
    }
  };

  if (newValue === Values.On) {
    changeCondition(ConditionEqualityType.Equal);
  } else if (newValue === Values.Off) {
    changeCondition(ConditionEqualityType.NotEqual);
  } else if (newValue === Values.Ignore) {
    const sourceCodeCondition = customConditions?.find((cc) => cc.filter.dimensionName === SourceCodeDimensionName);
    if (sourceCodeCondition !== undefined) {
      sourceCodeCondition.filter.equalityType = ConditionEqualityType.NotEqual;
      const index = customConditions.indexOf(sourceCodeCondition);
      customConditions.splice(index, 1);
    }
  }

  return customConditions;
}
